const PAYOUT_OP_VALUES = ['gt', 'gte', 'lt', 'lte', 'eq', 'between'];
const PARAM_OP_VALUES = ['equals', 'in', 'regex', 'numberRange'];
const FILTER_KEYS = ['payout', 'query_params', '$or'];

const isPair = (v: unknown) => Array.isArray(v) && v.length === 2 && v.every(n => typeof n === 'number');

/**
 * The engine skips any rule whose key is absent and allows by default, so a typo like
 * `event_type` does not error — it silently stops filtering and everything sends. That
 * is the failure this guards against; JSON.parse alone would accept it happily.
 */
export function validateFilter(obj: unknown, path = 'filter'): string[] {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return [`${path} must be an object`];
  const errors: string[] = [];
  const f = obj as Record<string, unknown>;

  for (const key of Object.keys(f)) {
    if (!FILTER_KEYS.includes(key)) {
      errors.push(`${path}.${key} is not a rule — expected one of ${FILTER_KEYS.join(', ')}`);
    }
  }

  if (f.payout !== undefined) {
    const p = f.payout as Record<string, unknown>;
    if (typeof p !== 'object' || p === null) errors.push(`${path}.payout must be an object`);
    else {
      if (!PAYOUT_OP_VALUES.includes(p.op as string)) errors.push(`${path}.payout.op must be one of ${PAYOUT_OP_VALUES.join(', ')}`);
      else if (p.op === 'between' ? !isPair(p.value) : typeof p.value !== 'number') {
        errors.push(`${path}.payout.value must be ${p.op === 'between' ? '[from, to]' : 'a number'}`);
      }
    }
  }

  if (f.query_params !== undefined) {
    if (!Array.isArray(f.query_params)) errors.push(`${path}.query_params must be an array`);
    else f.query_params.forEach((r, i) => {
      const at = `${path}.query_params[${i}]`;
      if (typeof r !== 'object' || r === null) return errors.push(`${at} must be an object`);
      const rule = r as Record<string, unknown>;
      if (typeof rule.name !== 'string' || !rule.name) errors.push(`${at}.name is required`);
      if (!PARAM_OP_VALUES.includes(rule.op as string)) errors.push(`${at}.op must be one of ${PARAM_OP_VALUES.join(', ')}`);
      else if (rule.op === 'in' && !Array.isArray(rule.value)) errors.push(`${at}.value must be an array for "in"`);
      else if (rule.op === 'numberRange' && !isPair(rule.value)) errors.push(`${at}.value must be [from, to] for "numberRange"`);
      else if ((rule.op === 'equals' || rule.op === 'regex') && typeof rule.value !== 'string') {
        errors.push(`${at}.value must be a string for "${rule.op}"`);
      }
    });
  }

  if (f.$or !== undefined) {
    if (!Array.isArray(f.$or)) errors.push(`${path}.$or must be an array of sub-filters`);
    else f.$or.forEach((sub, i) => errors.push(...validateFilter(sub, `${path}.$or[${i}]`)));
  }

  return errors;
}
